import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../environments/environment';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { UserAvatarService } from './user-avatar.service';
import { getOrCreateGuestId } from './guest-id';

const HEARTBEAT_MS = 45_000;
const HEARTBEAT_TIMEOUT_MS = 20_000;

@Injectable({ providedIn: 'root' })
export class PresenceService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly avatars = inject(UserAvatarService);
  private timer: ReturnType<typeof setInterval> | null = null;
  private started = false;
  private inFlight = false;
  private profileEnsureAttempts = 0;

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;
    void this.heartbeat();
    this.timer = setInterval(() => void this.heartbeat(), HEARTBEAT_MS);
    window.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('pagehide', this.onPageHide);
  }

  stop(): void {
    this.started = false;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    window.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('pagehide', this.onPageHide);
  }

  getGuestId(): string {
    return getOrCreateGuestId();
  }

  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      void this.heartbeat();
    }
  };

  private readonly onPageHide = (): void => {
    void this.heartbeat(true);
  };

  private async heartbeat(useBeacon = false): Promise<void> {
    if (this.inFlight && !useBeacon) {
      return;
    }

    const body = JSON.stringify({ guestId: this.getGuestId() });
    const url = `${environment.apiBaseUrl}/api/users/presence/heartbeat`;

    try {
      if (useBeacon && typeof navigator.sendBeacon === 'function' && !this.auth.isAuthenticated()) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
        return;
      }

      this.inFlight = true;
      let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      if (this.auth.isAuthenticated()) {
        const token = await this.auth.getToken();
        headers = headers.set('Authorization', `Bearer ${token}`);
      }

      await firstValueFrom(
        this.http
          .post(url, { guestId: this.getGuestId() }, { headers })
          .pipe(timeout(HEARTBEAT_TIMEOUT_MS))
      );

      if (this.auth.isAuthenticated() && this.profileEnsureAttempts < 3) {
        this.profileEnsureAttempts += 1;
        try {
          const profile = await this.api.getMyProfile();
          this.avatars.setCustomAvatarUrl(profile.avatarUrl);
          this.profileEnsureAttempts = 99;
        } catch {
          // retry on later heartbeats
        }
      }
    } catch {
      // presence is best-effort
    } finally {
      this.inFlight = false;
    }
  }
}
